/**
 * `memberapp/memberTabs.ts` — pure tab + screen derivation for the
 * member phone app (#1665, design §2/§3/§5.1).
 *
 * Single-source discipline (the `navLinks.ts` invariant, §1.2 of the
 * design): ONE canonical tab list that every surface derives from with
 * a uniform rule. The bottom tab bar renders `deriveMemberAppTabs()`
 * directly; the active-tab + travel-direction logic reads
 * `memberAppTabForPath()`; tests exercise both shapes without mounting
 * the shell.
 *
 * Component gates change tab CONTENT, never tab count (§1.3 — features
 * ship default-ON; a gate that removed a tab would make the app's shape
 * lie about the product's shape). `deriveMemberAppTabs()` therefore
 * takes no gate inputs by design — if a future phase is tempted to pass
 * one, the answer is a content-level empty state inside the tab.
 *
 * #2071 (lead phone app, B1) added a fourth tab, **Team**, for a lead's
 * view (`docs/agent-prompts/design-lead-in-miniapp-2026-10-10.md` §3).
 * #2945 (slice 4, founder Q1 (a)) widens it: every role on a ROSTER team
 * gets the Team tab; a Taskmap team keeps three tabs. The team's kind decides
 * the tab count (`showsTeamTab`), and the role decides what Team holds: Team
 * home for a lead, co-lead or observer (`showsLeadTeam`), Team today for a
 * member (`team/TeamTabScreen.tsx`). The `member_full_roster` gate still
 * changes only the content.
 *
 * #3063 (founder, 4 Nov 2026) — the **Week sheet** (`/me/sheet`) is its own
 * tab on a roster team, for every role: Inbox · Calendar · Week sheet · Tasks
 * · Team. It comes and goes with Team (the same `showsTeamTab` rule).
 */

import { hasLeadView } from "../lib/teamAccess";
import { teamPeopleScreenMeta } from "./team/peopleModel";

export type MemberAppTabKey = "inbox" | "calendar" | "sheet" | "tasks" | "team";

export interface MemberAppTabSpec {
  key: MemberAppTabKey;
  to: string;
  label: string;
  /** §2 — the tab's concept glyph (rendered by the tab bar). */
  glyph: "inbox" | "calendar" | "sheet" | "tasks" | "team";
  /** NavLink `end` — Calendar owns the bare `/me` index. */
  end: boolean;
}

/** The canonical list. Order is the bar order (§2): Inbox · Calendar ·
 *  Tasks, with the Week sheet (#3063) after the Calendar and Team (#2071,
 *  #2945) last on a roster team. */
const MEMBER_APP_TABS: readonly MemberAppTabSpec[] = [
  { key: "inbox", to: "/me/inbox", label: "Inbox", glyph: "inbox", end: false },
  { key: "calendar", to: "/me", label: "Calendar", glyph: "calendar", end: true },
  { key: "sheet", to: "/me/sheet", label: "Week sheet", glyph: "sheet", end: false },
  { key: "tasks", to: "/me/tasks", label: "Tasks", glyph: "tasks", end: false },
  { key: "team", to: "/me/team", label: "Team", glyph: "team", end: false },
];

/** The tabs only a roster team has (#2945 Team, #3063 Week sheet). */
const ROSTER_TEAM_TABS: ReadonlySet<MemberAppTabKey> = new Set(["sheet", "team"]);

const MEMBER_TABS_ONLY = MEMBER_APP_TABS.filter((t) => !ROSTER_TEAM_TABS.has(t.key));

/** The one derivation every renderer consumes. Three tabs bare; five
 *  (… · Week sheet · … · Team) when the selected workspace is a roster team
 *  (`showsTeamTab`, #2945: every role, not only a lead's view; #3063 adds the
 *  Week sheet). */
export function deriveMemberAppTabs({
  teamTab = false,
}: { teamTab?: boolean } = {}): readonly MemberAppTabSpec[] {
  return teamTab ? MEMBER_APP_TABS : MEMBER_TABS_ONLY;
}

type WorkspaceRole = { role?: string | null; team_kind?: string | null };

/**
 * #2945 — whether the selected workspace gets the Team tab (and, #3063, the
 * Week sheet tab): it is a roster team (a Taskmap team has no roster to
 * read), whatever the role. Lead, co-lead, observer and member alike.
 * `undefined` while the workspaces load: no tab yet, rather than a tab that
 * vanishes.
 */
export function showsTeamTab(workspace: WorkspaceRole | undefined): boolean {
  if (!workspace) return false;
  return workspace.team_kind !== "tasks";
}

/**
 * #2071 — whether the Team tab holds the lead's Team home (and its stacked
 * lead screens: Roster by day, Review, People): the caller has the lead's
 * view of a roster team (lead, co-lead, observer). A member's Team tab holds
 * Team today and the Week sheet instead (#2945).
 */
export function showsLeadTeam(workspace: WorkspaceRole | undefined): boolean {
  if (!workspace) return false;
  return hasLeadView(workspace.role) && workspace.team_kind !== "tasks";
}

/**
 * #2945 — whether the member app OPENS on Team (founder Q1 (a)): a member's
 * view of a roster team. A lead's view keeps the Calendar as its opening
 * screen; a Taskmap team has no Team tab.
 */
export function opensOnTeam(workspace: WorkspaceRole | undefined): boolean {
  return showsTeamTab(workspace) && !showsLeadTeam(workspace);
}

/**
 * Which tab a member-app path belongs to — drives the active-tab state
 * and the §6.3 travel-direction on tab switches. Stacked screens map to
 * the tab that owns them conceptually (§4.1): calendar screens keep
 * Calendar lit, request/ledger screens keep Tasks lit. Profile-sheet
 * screens (My numbers / My data / privacy / feed) return `null` — they
 * float over whichever tab pushed them, so no tab claims them.
 */
export function memberAppTabForPath(pathname: string): MemberAppTabKey | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/me" || path === "/me/schedule") return "calendar";
  if (path === "/me/inbox" || path === "/me/inbox/archive") return "inbox";
  if (path === "/me/tasks") return "tasks";
  // #2071 — the Team tab (a lead's Team home and its stacked screens under
  // `/me/team/…`); #2945 — a member's Team today.
  if (path === "/me/team" || path.startsWith("/me/team/")) return "team";
  if (path === "/me/whos-on" || path === "/me/roster") return "calendar";
  // #3063 — the Week sheet is its own tab.
  if (path === "/me/sheet") return "sheet";
  if (path === "/me/changes") return "calendar";
  if (/^\/me\/cycles\/[^/]+\/roster$/.test(path)) return "calendar";
  if (path === "/me/leaves" || path === "/me/swaps") return "tasks";
  // #1667 — the Requests ledger's stacked screens (new-request flow +
  // request detail) belong to Tasks.
  if (path === "/me/requests" || path.startsWith("/me/requests/")) return "tasks";
  // #2181 — the member's Fixed shifts (and the old per-window URL, which
  // redirects there) belong to Tasks, where the row that opens it lives.
  if (path === "/me/fixed-shifts") return "tasks";
  if (/^\/me\/cycles\/[^/]+\/propose$/.test(path)) return "tasks";
  // #1682 — the in-frame re-homes of the per-cycle submission form and
  // the per-shift feedback surface both belong to Tasks (§5.5.1: the
  // Standing group is where they're linked from).
  if (/^\/me\/cycles\/[^/]+\/submit$/.test(path)) return "tasks";
  if (/^\/me\/cycles\/[^/]+\/feedback$/.test(path)) return "tasks";
  return null;
}

/** Bar order index — the §6.3 travel direction is `next - prev`. */
export function memberAppTabIndex(key: MemberAppTabKey): number {
  return MEMBER_APP_TABS.findIndex((t) => t.key === key);
}

/**
 * Stacked-screen registry: title + back target for every non-tab
 * member-app route (§4.1 map). `backTo` is the DEFAULT pop target when
 * there is no in-app history (deep link straight onto a screen) — with
 * history, back pops normally. One table so the redirect contract
 * (§4.2 — zero 404s, every capability reachable) is enumerable in
 * tests.
 */
export interface MemberScreenMeta {
  title: string;
  backTo: string;
}

export function memberScreenMetaForPath(pathname: string): MemberScreenMeta | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  const fixed: Record<string, MemberScreenMeta> = {
    "/me/whos-on": { title: "Who's on", backTo: "/me" },
    // #1958 — the team roster by date window (`?from&to`).
    "/me/roster": { title: "Team roster", backTo: "/me" },
    "/me/changes": { title: "What changed", backTo: "/me" },
    "/me/numbers": { title: "My numbers", backTo: "/me" },
    "/me/hours": { title: "Hours", backTo: "/me/numbers" },
    "/me/statistics": { title: "Statistics", backTo: "/me/numbers" },
    "/me/insights": { title: "Insights", backTo: "/me/numbers" },
    "/me/comply": { title: "Comply", backTo: "/me/numbers" },
    "/me/forecast": { title: "Forecast", backTo: "/me/numbers" },
    "/me/audit": { title: "Activity", backTo: "/me" },
    "/me/export": { title: "Export your data", backTo: "/me" },
    "/me/privacy": { title: "Privacy", backTo: "/me" },
    "/me/feed": { title: "Add to my calendar", backTo: "/me" },
    // #2077 — Message your lead, from the profile sheet.
    "/me/message-lead": { title: "Message your lead", backTo: "/me" },
    // #2071 B3 — the lead's Review, pushed from Team home.
    "/me/team/review": { title: "Review", backTo: "/me/team" },
    "/me/leaves": { title: "Leave & requests", backTo: "/me/tasks" },
    "/me/swaps": { title: "Swaps", backTo: "/me/tasks" },
    // #1666 — the Inbox archive (§5.2.4) and the preferences screen the
    // transactional email footer finally points at (§7 rule 5). Preferences
    // pop back to Calendar rather than the Inbox: the profile sheet is the
    // only in-app route to it, and an emailed deep link has no history.
    "/me/inbox/archive": { title: "Archive", backTo: "/me/inbox" },
    "/me/notifications": { title: "Notification preferences", backTo: "/me" },
    // #2072 — Profile → Telegram (Connect / Disconnect).
    "/me/telegram": { title: "Telegram", backTo: "/me" },
    // #1667 — the new-request flow. `/me/leaves` keeps its meta above:
    // the route is now a redirect to /me/tasks, but the meta may render
    // for a frame before the redirect resolves.
    "/me/requests/new": { title: "New request", backTo: "/me/tasks" },
    // #2181 — the member's own fixed shifts, from today on.
    "/me/fixed-shifts": { title: "Fixed shifts", backTo: "/me/tasks" },
    // #2071 B2 — the lead's Roster by day, pushed from Team home.
    "/me/team/roster": { title: "Roster by day", backTo: "/me/team" },
    // #3064 — the lead's Stations, from Team home's Stations door.
    "/me/team/stations": { title: "Stations", backTo: "/me/team" },
  };
  if (fixed[path]) return fixed[path];
  // #2071 B6 — People and one person's messages, stacked over Team.
  const people = teamPeopleScreenMeta(path);
  if (people) return people;
  // #1958 — the old per-window grid URL redirects to `/me/roster?from&to`;
  // the meta shows for the frame before the redirect resolves.
  if (/^\/me\/cycles\/[^/]+\/roster$/.test(path)) {
    return { title: "Team roster", backTo: "/me" };
  }
  if (/^\/me\/cycles\/[^/]+\/propose$/.test(path)) {
    return { title: "Fixed shifts", backTo: "/me/tasks" };
  }
  // #1682 — the two frame-escape destinations re-homed in-frame: the
  // per-cycle submission form ("Note to your lead" on the Tasks tab)
  // and the per-shift feedback form (event sheet + Tasks "Feedback
  // given"). Both pop back to Tasks when deep-linked with no history.
  // #1701 — the title matches the row, and both match what the screen
  // actually collects since #1667 retired the availability grids.
  if (/^\/me\/cycles\/[^/]+\/submit$/.test(path)) {
    return { title: "Note to your lead", backTo: "/me/tasks" };
  }
  if (/^\/me\/cycles\/[^/]+\/feedback$/.test(path)) {
    return { title: "Shift feedback", backTo: "/me/tasks" };
  }
  // #1667 — one request's detail screen (`/me/requests/new` is caught
  // by the fixed table first).
  if (/^\/me\/requests\/[^/]+$/.test(path)) {
    return { title: "Request", backTo: "/me/tasks" };
  }
  return null;
}

/**
 * #2172 M0 — how deep a path is stacked: 0 for a tab (or a path the registry
 * does not know), else one more than its `backTo` parent (`/me/hours` →
 * `/me/numbers` → `/me` is 2). Drives the push / pop direction.
 */
export function memberScreenDepth(pathname: string): number {
  let depth = 0;
  let path = pathname.replace(/\/+$/, "") || "/";
  const seen = new Set<string>();
  for (;;) {
    const meta = memberScreenMetaForPath(path);
    if (!meta || seen.has(path)) return depth;
    seen.add(path);
    depth += 1;
    path = meta.backTo;
  }
}

/**
 * #2172 M0 — how the frame moves from one screen to the next:
 *   - `push` / `pop` — deeper / shallower in the stack (iOS: the new screen
 *     slides in over its parent; pop is the mirror). Two screens at the same
 *     depth push, unless the browser went back (`POP`).
 *   - `tab-forward` / `tab-back` — another tab, to the right / left in the
 *     bar.
 *   - `fade` — anything else (a path no tab claims).
 *   - `none` — the same tab at the root under another address (an alias
 *     redirect), or no move at all.
 */
export type MemberScreenTransitionKind =
  | "push"
  | "pop"
  | "tab-forward"
  | "tab-back"
  | "fade"
  | "none";

export function memberScreenTransition(
  from: string,
  to: string,
  navigationType: "POP" | "PUSH" | "REPLACE" = "PUSH",
): MemberScreenTransitionKind {
  if (from === to) return "none";
  const fromDepth = memberScreenDepth(from);
  const toDepth = memberScreenDepth(to);
  if (toDepth > fromDepth) return "push";
  if (toDepth < fromDepth) return "pop";
  if (toDepth > 0) return navigationType === "POP" ? "pop" : "push";
  const fromTab = memberAppTabForPath(from);
  const toTab = memberAppTabForPath(to);
  if (fromTab && toTab) {
    if (fromTab === toTab) return "none";
    return memberAppTabIndex(toTab) > memberAppTabIndex(fromTab) ? "tab-forward" : "tab-back";
  }
  return "fade";
}
